/**
 * TrackerModelFormDialog - Cadastro/edição de modelo de rastreador
 *
 * O fabricante vem do cadastro de Pessoas (papel Fabricante), evitando
 * cadastro duplicado do mesmo CNPJ (BR-PES-001).
 *
 * RELEASE: 2026-10-06 v1.1.1 - Identificação do atalho e tecnologias selecionadas visíveis abaixo do campo.
 * RELEASE: 2026-10-06 v1.1.0 - Conectividade múltipla, busca e cadastro rápido de fabricante.
 * RELEASE: 2026-10-06 v1.0.0 - Versão inicial (Fase 2 do Estoque).
 */
import { useEffect, useState } from 'react';
import { ChevronDown, Plus, X } from 'lucide-react';
import { z } from 'zod';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { PersonFormDialog } from '@/components/persons/PersonFormDialog';
import { PersonSelector } from '@/components/persons/PersonSelector';
import { usePermissions } from '@/hooks/usePermissions';
import {
  TRACKER_CONNECTIVITY_TECHNOLOGIES,
  useStockRegistryMutations,
  type TrackerConnectivityTechnology,
  type TrackerModel,
} from '@/hooks/useStockRegistries';

const modelSchema = z.object({
  name: z.string().trim().min(1, 'Informe o nome do modelo').max(120, 'O modelo deve ter no máximo 120 caracteres'),
  notes: z.string().max(1000, 'As observações devem ter no máximo 1.000 caracteres'),
});

interface Props {
  open: boolean;
  model: TrackerModel | null;
  onOpenChange: (open: boolean) => void;
  /** 2026-10-08: usado pelo atalho "+" para já selecionar o modelo criado. */
  onSuccess?: (id: string, name: string) => void;
}

export function TrackerModelFormDialog({ open, model, onOpenChange, onSuccess }: Props) {
  const [name, setName] = useState('');
  const [manufacturer, setManufacturer] = useState<string | null>(null);
  const [manufacturerLabel, setManufacturerLabel] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [connectivity, setConnectivity] = useState<TrackerConnectivityTechnology[]>([]);
  const [showManufacturerForm, setShowManufacturerForm] = useState(false);
  const { canCreate } = usePermissions();
  const { upsertModel } = useStockRegistryMutations();
  const canCreateManufacturer = canCreate('persons_manufacturers');

  useEffect(() => {
    if (!open) return;
    setName(model?.name ?? '');
    setManufacturer(model?.manufacturer_person_id ?? null);
    setManufacturerLabel(model?.manufacturer_name ?? null);
    setNotes(model?.notes ?? '');
    setConnectivity(model?.connectivity_technologies ?? []);
  }, [open, model]);

  const save = () => {
    const parsed = modelSchema.safeParse({ name, notes });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? 'Revise os campos informados');
      return;
    }
    upsertModel.mutate(
      {
        id: model?.id ?? null,
        data: {
          name: parsed.data.name,
          notes: parsed.data.notes,
          manufacturer_person_id: manufacturer,
          connectivity_technologies: connectivity,
          is_active: model?.is_active ?? true,
        },
      },
      { onSuccess: (savedId) => { if (savedId) onSuccess?.(savedId, parsed.data.name); onOpenChange(false); } },
    );
  };

  const toggleConnectivity = (technology: TrackerConnectivityTechnology, checked: boolean) => {
    setConnectivity((current) => checked
      ? [...current, technology]
      : current.filter((item) => item !== technology));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{model ? 'Editar modelo' : 'Novo modelo de rastreador'}</DialogTitle>
          <DialogDescription>Configure o fabricante, o modelo e suas tecnologias de conectividade.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Fabricante</Label>
            <div className="flex gap-2">
              <PersonSelector
                value={manufacturer}
                onSelect={(personId) => {
                  setManufacturer(personId);
                  if (!personId) setManufacturerLabel(null);
                }}
                role="manufacturer"
                source="stock-manufacturers"
                placeholder="Sem fabricante"
                selectedLabel={manufacturerLabel}
              />
              {canCreateManufacturer && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    {/* Ícone vetorial mantém o atalho visível sem depender de fonte externa. */}
                    <Button type="button" variant="outline" size="icon" className="shrink-0" onClick={() => setShowManufacturerForm(true)} aria-label="Atalho: cadastrar novo fabricante" title="Atalho: cadastrar novo fabricante">
                      <Plus className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Atalho: cadastrar novo fabricante</TooltipContent>
                </Tooltip>
              )}
            </div>
            <p className="text-xs text-muted-foreground">Lista as pessoas ativas cadastradas em Pessoas › Fabricantes.</p>
          </div>
          <div className="space-y-1.5">
            <Label>Modelo *</Label>
            <Input value={name} maxLength={120} onChange={(e) => setName(e.target.value)} placeholder="Ex.: ST4315" />
          </div>
          <div className="space-y-1.5">
            <Label>Tecnologia Conectividade</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button type="button" variant="outline" className="w-full justify-between font-normal">
                  {connectivity.length} tecnologia(s) selecionada(s)
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-4" align="start">
                <p className="mb-3 text-sm font-semibold">Tecnologias de Conectividade</p>
                <div className="space-y-3">
                  {TRACKER_CONNECTIVITY_TECHNOLOGIES.map((technology) => (
                    <label key={technology.value} className="flex cursor-pointer items-center gap-2 text-sm">
                      <Checkbox
                        checked={connectivity.includes(technology.value)}
                        onCheckedChange={(checked) => toggleConnectivity(technology.value, checked === true)}
                      />
                      {technology.label}
                    </label>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
            {/* Seleções ficam visíveis ao abrir a ficha e podem ser removidas individualmente. */}
            {connectivity.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-1" aria-label="Tecnologias selecionadas">
                {TRACKER_CONNECTIVITY_TECHNOLOGIES.filter((technology) => connectivity.includes(technology.value)).map((technology) => (
                  <Button
                    key={technology.value}
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="h-7 gap-2 rounded-full px-3 text-xs"
                    aria-label={`Remover ${technology.label}`}
                    title={`Remover ${technology.label}`}
                    onClick={() => toggleConnectivity(technology.value, false)}
                  >
                    {technology.label}
                    <X className="h-3 w-3" aria-hidden="true" />
                  </Button>
                ))}
              </div>
            )}
          </div>
          <div className="space-y-1.5">
            <Label>Observações</Label>
            <Textarea value={notes} maxLength={1000} rows={3} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} disabled={upsertModel.isPending}>Salvar</Button>
        </DialogFooter>
      </DialogContent>
      <PersonFormDialog
        open={showManufacturerForm}
        onOpenChange={setShowManufacturerForm}
        person={null}
        defaultRole="manufacturer"
        onOpenExisting={() => undefined}
        onSuccess={(personId, personName) => {
          setManufacturer(personId);
          setManufacturerLabel(personName);
          setShowManufacturerForm(false);
        }}
      />
    </Dialog>
  );
}
